import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";

import type { ReviewQueueView } from "@/features/review-queue/types";
import type { ActionType } from "@/lib/vocabulary";

import { PermissionGuard } from "@/components/shared/guards/permission-guard";
import { ReviewQueuePage } from "@/features/review-queue";
import { REVIEW_QUEUE_VIEW_VALUES } from "@/features/review-queue/types";
import { ACTION_TYPE } from "@/lib/vocabulary";

const ACTION_TYPE_TUPLE = Object.values(ACTION_TYPE) as [ActionType, ...ActionType[]];
const reviewQueueViewTuple = REVIEW_QUEUE_VIEW_VALUES as [
  ReviewQueueView,
  ...ReviewQueueView[],
];

/**
 * URL-synced list state: filters, search, and pagination all live in the
 * query string so a filtered view is shareable and survives a refresh —
 * the same standard every list route in the app follows (see
 * `/agent-inventory`). `tier` is a bounded 0-4 integer per the consequence
 * tier vocabulary.
 */
const reviewQueueSearchSchema = z.object({
  page: z.coerce.number().int().min(1).catch(1),
  limit: z.coerce.number().int().min(1).max(100).catch(20),
  q: z.string().optional().catch(undefined),
  tier: z.coerce.number().int().min(0).max(4).optional().catch(undefined),
  actionType: z.enum(ACTION_TYPE_TUPLE).optional().catch(undefined),
  assignment: z.enum(["all", "assigned", "unassigned"]).optional().catch(undefined),
  urgency: z.enum(["all", "overdue", "urgent"]).optional().catch(undefined),
  environment: z.string().optional().catch(undefined),
  view: z.enum(reviewQueueViewTuple).optional().catch(undefined),
  sort: z.string().optional().catch(undefined),
});

export const Route = createFileRoute("/review-queue/")({
  validateSearch: reviewQueueSearchSchema,
  component: () => (
    <PermissionGuard module="review_queue">
      <ReviewQueuePage />
    </PermissionGuard>
  ),
});
